import {
  fetchConversationEvents,
  type MessageOrder,
} from '@/services/conversationService';
import { ChatMessageOutput } from '@/services/websocketService';
import {
  CACHE_KEYS,
  CACHE_PREFIXES,
  PaginatedCache,
  type CacheEntry,
} from '@/utils/cache';
import { get as idbGet, set as idbSet } from 'idb-keyval';
import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Message store with built-in caching for conversation events
 * Handles loading, pagination, and real-time updates of messages
 * Cache key pattern: "${conversationId}_${limit}_${offset}"
 * TTL: 10 minutes (messages don't change as frequently as conversation list)
 */
export const useMessageStore = defineStore('messages', () => {
  // Message cache instance (10 minute TTL)
  const messageCache = new PaginatedCache<ChatMessageOutput>({
    ttl: 10 * 60 * 1000,
    persist: true,
    storageKeyPrefix: CACHE_PREFIXES.MESSAGES,
  });

  // State
  const currentMessages = ref<ChatMessageOutput[]>([]);
  const currentConversationId = ref<string | null>(null);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const hasMore = ref(false);
  const currentOffset = ref(0);
  const limit = 50; // Fixed message limit per request

  const error = ref<string | null>(null);
  const pendingFirstMessage = ref<string | null>(null);

  /**
   * Generate cache key for messages
   */
  const generateCacheKey = (conversationId: string, offset: number): string => {
    return messageCache.generateKey({
      conversationId,
      limit,
      offset,
    });
  };

  /**
   * Load messages for a conversation
   * Checks cache first, fetches from API if not cached
   * Replaces existing messages (initial load)
   */
  const loadMessages = async (
    conversationId: string,
    order: MessageOrder = 'desc',
  ): Promise<{ conversation_id: string; agent_id: string } | undefined> => {
    try {
      isLoading.value = true;
      error.value = null;
      currentConversationId.value = conversationId;
      currentOffset.value = 0;

      const cacheKey = generateCacheKey(conversationId, 0);

      // Check cache first
      const cachedEntry = await messageCache.get(cacheKey);
      if (cachedEntry) {
        currentMessages.value = cachedEntry.data;
        hasMore.value = cachedEntry.hasMore;
        currentOffset.value = cachedEntry.data.length;
        return idbGet(CACHE_KEYS.CONVERSATION_METADATA(conversationId));
      }

      // Cache miss - fetch from API
      const response = await fetchConversationEvents(
        conversationId,
        limit,
        0,
        order,
      );

      // Reverse to display in ascending order (oldest to newest)
      const messages = response.data.reverse();

      // Store in cache
      const cacheEntry: CacheEntry<ChatMessageOutput> = {
        data: messages,
        total: response.total,
        hasMore: response.has_more,
        timestamp: Date.now(),
      };
      messageCache.set(cacheKey, cacheEntry);

      // Update state
      currentMessages.value = messages;
      hasMore.value = response.has_more;
      currentOffset.value = messages.length;

      const conversationCacheEntry = {
        conversation_id: response.conversation.id,
        agent_id: response.conversation.agent.id,
      };
      idbSet(
        CACHE_KEYS.CONVERSATION_METADATA(response.conversation.id),
        conversationCacheEntry,
      );
      return conversationCacheEntry;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load messages';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Load older messages for pagination
   * Prepends older messages to the beginning
   * Does not replace existing messages
   */
  const loadMoreMessages = async (): Promise<void> => {
    if (!currentConversationId.value || isLoadingMore.value || !hasMore.value) {
      return;
    }

    try {
      isLoadingMore.value = true;
      error.value = null;

      const cacheKey = generateCacheKey(
        currentConversationId.value,
        currentOffset.value,
      );

      // Check cache first
      const cachedEntry = await messageCache.get(cacheKey);
      if (cachedEntry) {
        // Prepend cached older messages
        currentMessages.value = [...cachedEntry.data, ...currentMessages.value];
        hasMore.value = cachedEntry.hasMore;
        currentOffset.value += cachedEntry.data.length;
        return;
      }

      // Cache miss - fetch from API
      const response = await fetchConversationEvents(
        currentConversationId.value,
        limit,
        currentOffset.value,
        'desc',
      );

      // Reverse to get ascending order
      const olderMessages = response.data.reverse();

      // Store in cache
      const cacheEntry: CacheEntry<ChatMessageOutput> = {
        data: olderMessages,
        total: response.total,
        hasMore: response.has_more,
        timestamp: Date.now(),
      };
      messageCache.set(cacheKey, cacheEntry);

      // Prepend older messages to beginning
      currentMessages.value = [...olderMessages, ...currentMessages.value];
      hasMore.value = response.has_more;
      currentOffset.value += olderMessages.length;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load more messages';
      throw err;
    } finally {
      isLoadingMore.value = false;
    }
  };

  /**
   * Add a new message (for real-time WebSocket updates)
   * Appends to the end without touching cache
   */
  const addMessage = (message: ChatMessageOutput): void => {
    // Avoid duplicates (temporary IDs might be replaced by server IDs)
    const isDuplicate = currentMessages.value.some(
      (m) => m.message_id === message.message_id,
    );
    if (!isDuplicate) {
      currentMessages.value.push(message);
    }
  };

  /**
   * Clear messages for a specific conversation from cache
   * Useful for invalidating conversation-specific data
   */
  const invalidateConversationMessages = (conversationId: string): void => {
    // Pattern to match all cache entries for this conversation
    const pattern = new RegExp(`^${conversationId}_`);
    messageCache.invalidatePattern(pattern);
    console.log(
      `Message cache invalidated for the conversation ${conversationId}`,
    );
  };

  /**
   * Refresh messages from the API (bypass cache)
   */
  const refreshMessages = async (
    order: MessageOrder = 'desc',
  ): Promise<void> => {
    if (!currentConversationId.value) {
      return;
    }

    // Invalidate current conversation cache
    invalidateConversationMessages(currentConversationId.value);

    // Reload from API
    await loadMessages(currentConversationId.value, order);
  };

  /**
   * Clear all message state
   */
  const clearMessages = (): void => {
    currentMessages.value = [];
    currentConversationId.value = null;
    currentOffset.value = 0;
    hasMore.value = false;
    error.value = null;
  };

  /**
   * Clear entire message cache
   */
  const clearCache = (): void => {
    messageCache.invalidateAll();
  };

  /**
   * Set error message
   */
  const setError = (message: string | null): void => {
    error.value = message;
  };

  return {
    // State
    currentMessages,
    currentConversationId,
    isLoading,
    isLoadingMore,
    hasMore,
    currentOffset,
    limit,
    error,
    pendingFirstMessage,

    // Actions
    loadMessages,
    loadMoreMessages,
    addMessage,
    invalidateConversationMessages,
    refreshMessages,
    clearMessages,
    clearCache,
    setError,
  };
});
